import React, { useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  Button,
  Modal,
  TextLinkifyTags,
  theme,
  VStack,
} from '@sg/garnish';

import { useScanToPayModalSafeAreaInsets } from '../../hooks';
import { ScanToPayFaqEntry } from '../ScanToPayFaqEntry';

export const ScanToPayFaqModal = (props: ScanToPayFaqModalProps) => {
  const { isVisible, faq, onForgotToScan, onContactSupport, onRequestClose } =
    props;

  const safeAreaStyle = useScanToPayModalSafeAreaInsets();

  const { formatMessage } = useIntl();
  const links = useMemo(
    () => [
      {
        tag: 'contact_support',
        description: formatMessage(messages.contactSupportTag),
        underline: true,
        onPress: onContactSupport,
      },
    ],
    [formatMessage, onContactSupport],
  );

  return (
    <Modal
      size="medium"
      palette="neutral"
      fitHeight
      withSafeAreaView={false}
      visible={isVisible}
      style={styles.container}
      onRequestClose={onRequestClose}
    >
      <View style={[styles.container, safeAreaStyle]}>
        <View style={styles.wrapper}>
          <Modal.CloseBtn
            palette="muted"
            onPress={onRequestClose}
            width={40}
            height={40}
          />

          <View style={styles.content}>
            <BodyText sizeMatch={['18']}>
              <FormattedMessage {...messages.title} />
            </BodyText>

            <VStack hasDivider gap={0}>
              {faq.map((entry) => (
                <ScanToPayFaqEntry
                  key={entry.title}
                  title={entry.title}
                  image={entry.image}
                />
              ))}
            </VStack>
          </View>
        </View>

        <Modal.Footer withoutTopBorder style={styles.footer}>
          {onForgotToScan ? (
            <Button
              width="100%"
              palette="white"
              size="large"
              onPress={onForgotToScan}
            >
              <FormattedMessage {...messages.forgotToScan} />
            </Button>
          ) : null}

          <TextLinkifyTags
            sizeMatch={['14']}
            underline
            style={styles.contactSupport}
            links={links}
          >
            {formatMessage(messages.contactSupport, {
              contact_support: '{contact_support}',
            })}
          </TextLinkifyTags>
        </Modal.Footer>
      </View>
    </Modal>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'How it works',
    description: 'Scan to Pay | FAQ | Title',
  },
  forgotToScan: {
    defaultMessage: 'Forgot to scan?',
    description: 'Scan to Pay | FAQ | CTA',
  },
  contactSupport: {
    defaultMessage: 'For other questions, {contact_support}.',
    description: 'Scan to Pay | FAQ | Contact support',
  },
  contactSupportTag: {
    defaultMessage: 'contact support',
    description: 'Scan to Pay | FAQ | Contact support tag',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.FOREST,
  },
  wrapper: {
    backgroundColor: theme.colors.CREAM,
  },
  content: {
    gap: theme.spacing['3'],
    paddingHorizontal: theme.spacing['6'],
    marginBottom: theme.spacing['4'],
  },
  footer: {
    alignItems: 'center',
    gap: theme.spacing['8'],
    paddingBottom: 0,
    paddingTop: theme.spacing['6'],
    paddingHorizontal: theme.spacing['6'],
    backgroundColor: theme.colors.FOREST,
  },
  contactSupport: {
    color: theme.colors.WHITE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ScanToPayFaqModalProps = {
  faq: FaqEntry[];
  isVisible: boolean;
  onForgotToScan?: () => void;
  onContactSupport: () => void;
  onRequestClose: () => void;
};

type FaqEntry = {
  title: string;
  image: string;
};
